import { useEffect } from "react";

// Scroll the actual content container (the app usually scrolls its main pane),
// using the visible part of the hovered list as the drag target.
function scrollTarget(list, x, y) {
  const rect = list.getBoundingClientRect();
  let top = Math.max(0, rect.top);
  let bottom = Math.min(window.innerHeight, rect.bottom);
  let left = Math.max(0, rect.left);
  let right = Math.min(window.innerWidth, rect.right);
  const containers = [];
  const page = document.scrollingElement || document.documentElement;
  for (let element = list; element; element = element.parentElement) {
    const style = window.getComputedStyle(element);
    const bounds = element.getBoundingClientRect();
    if (element !== page) {
      if (/(auto|scroll|hidden|clip|overlay)/.test(style.overflowY)) {
        top = Math.max(top, bounds.top + element.clientTop);
        bottom = Math.min(bottom, bounds.top + element.clientTop + element.clientHeight);
      }
      if (/(auto|scroll|hidden|clip|overlay)/.test(style.overflowX)) {
        left = Math.max(left, bounds.left + element.clientLeft);
        right = Math.min(right, bounds.left + element.clientLeft + element.clientWidth);
      }
    }
    if ((element === page || /(auto|scroll|overlay)/.test(style.overflowY)) &&
        element.scrollHeight > element.clientHeight) containers.push(element);
  }
  // The app bar is a sibling overlay, not a clipping ancestor. Its covered area
  // cannot receive card drag events, so start the top edge below the bar.
  const appBar = document.querySelector("[data-app-topbar]");
  if (appBar) {
    const bounds = appBar.getBoundingClientRect();
    if (x >= bounds.left && x <= bounds.right && bounds.top <= top) {
      top = Math.max(top, bounds.bottom);
    }
  }
  if (bottom <= top || right <= left || x < left || x > right || y < top || y > bottom) return null;
  const edge = Math.min(64, (bottom - top) / 3);
  const direction = y < top + edge ? -1 : y > bottom - edge ? 1 : 0;
  if (!direction) return null;
  const container = containers.find((element) => {
    const remaining = direction < 0 ? element.scrollTop : element.scrollHeight - element.clientHeight - element.scrollTop;
    const hiddenCards = element === list || (direction < 0 ? rect.top < top - 1 : rect.bottom > bottom + 1);
    return remaining > 0 && hiddenCards;
  });
  if (!container) return null;
  const distance = direction < 0 ? y - top : bottom - y;
  return { container, speed: direction * (60 + 120 * (1 - distance / edge)) };
}

export default function useCardDragScroll(boardRef, enabled) {
  useEffect(() => {
    if (!enabled) return;
    let pointer = null;
    let frame = null;
    let previousTime = null;
    const stop = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      previousTime = null;
      pointer = null;
    };
    const tick = (time) => {
      frame = null;
      if (!pointer || !boardRef.current || !boardRef.current.contains(pointer.list)) return stop();
      const target = scrollTarget(pointer.list, pointer.x, pointer.y);
      if (!target) return stop();
      const elapsed = previousTime === null ? 16 : Math.min(32, time - previousTime);
      previousTime = time;
      const { container, speed } = target;
      container.scrollTop = Math.max(0, Math.min(
        container.scrollHeight - container.clientHeight,
        container.scrollTop + speed * elapsed / 1000,
      ));
      frame = window.requestAnimationFrame(tick);
    };
    const over = (event) => {
      const list = event.target instanceof Element ? event.target.closest("[data-list-id]") : null;
      if (!list || !boardRef.current || !boardRef.current.contains(list) ||
          !scrollTarget(list, event.clientX, event.clientY)) return stop();
      pointer = { list, x: event.clientX, y: event.clientY };
      if (frame === null) frame = window.requestAnimationFrame(tick);
    };
    const leave = (event) => {
      if ((event.relatedTarget && boardRef.current && !boardRef.current.contains(event.relatedTarget)) ||
          (!event.relatedTarget && (event.clientX <= 0 || event.clientY <= 0 ||
            event.clientX >= window.innerWidth || event.clientY >= window.innerHeight))) stop();
    };
    // Capture also sees events stopped by a card's insertion-point handlers.
    document.addEventListener("dragover", over, true);
    document.addEventListener("dragleave", leave, true);
    document.addEventListener("drop", stop, true);
    document.addEventListener("dragend", stop, true);
    window.addEventListener("blur", stop);
    return () => {
      stop();
      document.removeEventListener("dragover", over, true);
      document.removeEventListener("dragleave", leave, true);
      document.removeEventListener("drop", stop, true);
      document.removeEventListener("dragend", stop, true);
      window.removeEventListener("blur", stop);
    };
  }, [boardRef, enabled]);
}
